import { msg } from 'gt-react';
import { ChartLineUpIcon } from '@phosphor-icons/react';
import {
  createRoute,
  lazyRouteComponent,
  redirect,
} from '@tanstack/react-router';
import { z } from 'zod';
import { Action, Subject } from '@erp/authz';
import { ProductModule } from '@erp/shared-types';
import {
  Route as pathlessAuthenticatedRoute,
  requireModules,
} from '../../route';
import { ForecastNavItems } from '../forecast/ForecastNavItems';

const DemandForecastingLayoutPage = lazyRouteComponent(
  () => import('./DemandForecastingLayoutPage'),
  'DemandForecastingLayoutPage',
);
const GeneralSettingsPage = lazyRouteComponent(
  () => import('./general/GeneralSettingsPage'),
  'GeneralSettingsPage',
);
const OverridesPage = lazyRouteComponent(
  () => import('./overrides/OverridesPage'),
  'OverridesPage',
);
const LeadTimePage = lazyRouteComponent(
  () => import('./lead-time/LeadTimePage'),
  'LeadTimePage',
);
const SurplusCompositionEditor = lazyRouteComponent(
  () => import('./surplus/SurplusCompositionEditor'),
  'SurplusCompositionEditor',
);
const OutlierRulesPage = lazyRouteComponent(
  () => import('./outlier-rules/OutlierRulesPage'),
  'OutlierRulesPage',
);
const SalesHistoryTransfersPage = lazyRouteComponent(
  () => import('./sales-history-transfers/SalesHistoryTransfersPage'),
  'SalesHistoryTransfersPage',
);
const RunHistoryPage = lazyRouteComponent(
  () => import('./runs/RunHistoryPage'),
  'RunHistoryPage',
);

/**
 * Demand forecasting: the settings that shape every forecast, one tab each —
 * the org-wide General settings, live overrides, lead-time components,
 * outlier rules, sales history transfers, and the engine's run history.
 * Summary and Details are where a
 * forecast is READ; they stay under the shared procurement-filters route and
 * are this nav entry's sub-items.
 *
 * The tabs are declared here rather than in their own modules because a child
 * route in a separate file cannot state its parent's path segments, which
 * `custom/tanstack-route-file-path` checks the file location against (the
 * Admin → Maintenance layout does the same).
 */
export const Route = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/procurement/demand-forecasting',
  staticData: {
    app: {
      nav: {
        label: msg('Demand forecasting', {
          context: 'sidebar nav label, Procurement section',
        }),
        icon: <ChartLineUpIcon className="h-4 w-4" weight="fill" />,
        group: 'Procurement',
        order: 39,
        groupOrder: 30,
        subItems: <ForecastNavItems />,
      },
      access: {
        modulesAnyOf: [ProductModule.PROCUREMENT],
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.Inventory }],
      },
    },
  },
  component: DemandForecastingLayoutPage,
  loader: () => ({
    crumb: msg('Demand forecasting', { context: 'page breadcrumb' }),
  }),
  beforeLoad: ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.PROCUREMENT],
    });
  },
});

/** Bare `/procurement/demand-forecasting` opens Overrides, the day-to-day tab. */
export const DemandForecastingIndexRoute = createRoute({
  getParentRoute: () => Route,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/procurement/demand-forecasting/overrides' });
  },
});

export const DemandForecastingGeneralRoute = createRoute({
  getParentRoute: () => Route,
  path: 'general',
  component: GeneralSettingsPage,
  loader: () => ({
    crumb: msg('General', { context: 'page breadcrumb' }),
  }),
});

export const DemandForecastingOverridesRoute = createRoute({
  getParentRoute: () => Route,
  path: 'overrides',
  component: OverridesPage,
  loader: () => ({
    crumb: msg('Overrides', { context: 'page breadcrumb' }),
  }),
});

export const DemandForecastingLeadTimeRoute = createRoute({
  getParentRoute: () => Route,
  path: 'lead-time',
  component: LeadTimePage,
  loader: () => ({
    crumb: msg('Lead Time', { context: 'page breadcrumb' }),
  }),
});

export const DemandForecastingSurplusRoute = createRoute({
  getParentRoute: () => Route,
  path: 'surplus',
  component: SurplusCompositionEditor,
  loader: () => ({
    crumb: msg('Surplus', { context: 'surplus settings breadcrumb' }),
  }),
});

export const DemandForecastingOutlierRulesRoute = createRoute({
  getParentRoute: () => Route,
  path: 'outlier-rules',
  component: OutlierRulesPage,
  loader: () => ({
    crumb: msg('Outlier rules', { context: 'page breadcrumb' }),
  }),
});

export const DemandForecastingSalesHistoryTransfersRoute = createRoute({
  getParentRoute: () => Route,
  path: 'sales-history-transfers',
  component: SalesHistoryTransfersPage,
  loader: () => ({
    crumb: msg('Sales history transfers', { context: 'page breadcrumb' }),
  }),
});

export const DemandForecastingRunsRoute = createRoute({
  getParentRoute: () => Route,
  path: 'runs',
  /** Which run's diagnostics to open with. Absent = the latest. */
  validateSearch: z.object({ runId: z.uuid().optional() }),
  component: RunHistoryPage,
  loader: () => ({
    crumb: msg('Run history', { context: 'page breadcrumb' }),
  }),
});
